import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
});

// ─── Super-admin impersonation headers ───────────────────────────────
// Module-level מצב שנקבע ע"י ImpersonationContext (ב-memory בלבד).
// לא localStorage — כך שרענון דף מאפס ולא נשאר "תקוע" באימפרסונציה.
let impersonationOrgId: string | null = null;
let impersonationAllowWrite = false;

export function setImpersonationHeaders(next: { orgId: string | null; allowWrite: boolean }) {
  impersonationOrgId = next.orgId;
  impersonationAllowWrite = next.allowWrite;
}

/**
 * Current impersonation headers as a plain object — for the rare raw `fetch`
 * calls that the axios `api` instance can't cover (keepalive autosaves on
 * unload, streaming readers). Empty when not impersonating.
 */
export function getImpersonationHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (impersonationOrgId) {
    h['X-Impersonate-Org'] = impersonationOrgId;
    if (impersonationAllowWrite) h['X-Impersonate-Allow-Write'] = 'true';
  }
  return h;
}

/**
 * Friendly Hebrew message from an axios error. Prefers the server's `message`
 * over the technical `error` code, and special-cases the impersonation
 * read-only guard (every write a super-admin makes while impersonating an org
 * is blocked unless "מצב עריכה" is on) so users see a clear instruction
 * instead of the raw `impersonation_read_only` code.
 */
export function apiErrorMessage(e: any, fallback = 'שגיאה'): string {
  const data = e?.response?.data;
  if (data?.error === 'impersonation_read_only') {
    return 'אתה צופה בעמותה במצב קריאה-בלבד. הפעל "מצב עריכה" (בפס האימפרסונציה למעלה) כדי לבצע שינויים.';
  }
  return data?.message || data?.error || e?.message || fallback;
}

// הוסף token לכל בקשה
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // הוספת כותרות impersonation רק כאשר פעיל.
  // אי-הוספה מונעת מ-backend-ים שלא מכירים את הכותרת ליפול.
  if (impersonationOrgId) {
    config.headers['X-Impersonate-Org'] = impersonationOrgId;
    if (impersonationAllowWrite) {
      config.headers['X-Impersonate-Allow-Write'] = 'true';
    }
  }
  return config;
});

/**
 * Mint a fresh 7-day JWT from the current (still valid) one and store it.
 * Used by the 401 interceptor below, and proactively by long-lived screens:
 * the call-center dialer refreshes hourly so a token can never expire
 * mid-shift. /auth/refresh-token itself requires a valid token, so once a
 * token HAS expired the only way back is a full login — which drops the
 * softphone registration.
 */
export async function refreshAuthToken(): Promise<string> {
  const currentToken = localStorage.getItem('token');
  if (!currentToken) throw new Error('no token to refresh');
  const res = await axios.post(`${API_URL}/auth/refresh-token`, {}, {
    headers: { Authorization: `Bearer ${currentToken}` },
  });
  const newToken = res.data.token as string;
  localStorage.setItem('token', newToken);
  api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
  return newToken;
}

// Response interceptor — טיפול ב-401 (token פג / לא תקין)
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(p => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // טיפול בנעילת חשבון
    if (error.response?.data?.error === 'account_locked') {
      if (!window.location.pathname.includes('/billing')) {
        window.location.href = '/billing?locked=true';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // נסה refresh אחד
      if (isRefreshing) {
        // אם כבר מרענן — תור
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAuthToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      } catch (refreshErr) {
        // refresh נכשל — התנתקות
        processQueue(refreshErr, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // הפנה ל-login עם הודעה
        window.location.href = '/login?reason=session_expired';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
